<template>
  <BContainer
    class="mt-2"
    fluid
  >
    <!-- Filters -->
    <BRow>
      <BCol
        cols="12"
        md="6"
        class="px-1"
      >
        <BFormGroup
          label="Search"
          label-for="filterHistorySearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistorySearch"
            v-model="tableHistories.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Status"
          label-for="filterHistoryStatus"
          label-class="font-weight-bolder"
        >
          <BFormSelect
            id="filterHistoryStatus"
            v-model="tableHistories.filter.status"
            :options="tableHistories.filterOptions.statuses"
            debounce="1000"
          >
            <template #first>
              <BFormSelectOption
                :value="null"
                disabled
              >
                -- select status here --
              </BFormSelectOption>
            </template>
          </BFormSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="6"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Approval From"
          label-for="filterHistoryDateFrom"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistoryDateFrom"
            v-model="tableHistories.filter.date_from"
            :max="tableHistories.filter.date_to"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="6"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Approval To"
          label-for="filterHistoryDateTo"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistoryDateTo"
            v-model="tableHistories.filter.date_to"
            :min="tableHistories.filter.date_from"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>
      <BCol
        class="px-1 mb-2 d-flex align-items-end pb-1"
        order="3"
      >
        <BOverlay
          rounded
          opacity="0.1"
          spinner-small
          class="d-inline"
          spinner-variant="warning"
          :show="exporter.excel.exporting"
          style="margin-bottom: 1.1px;"
        >
          <json-excel
            :class="[
              'btn btn-sm btn-success', {
                'btn-outline-success disabled': exporter.excel.exporting || !isValidForExport
              }
            ]"
            :fetch="excelProvider"
            :before-generate="onStartExcelExport"
            :before-finish="onFinishExcelExport"
            :name="excelFileName"
            :fields="exporter.excel.fields"
            :escape-csv="exporter.excel.escape"
            :type="exporter.excel.type"
            :header="exporter.excel.header"
            :footer="exporter.excel.footer"
            worksheet="Scheduled Deliveries"
          >
            {{ exporter.excel.exporting ? 'Exporting' : 'Export As Excel' }}
          </json-excel>
        </BOverlay>
      </BCol>
    </BRow>

    <!-- Table -->
    <BRow
      v-if="Number(tabIndex) === 1"
      class="mt-1"
    >
      <BCol
        cols="12"
        class="px-1"
      >
        <BTable
          ref="tableHistories"
          small
          hover
          striped
          bordered
          responsive
          show-empty
          :items="tableProvider"
          :busy="tableHistories.busy"
          :filter="tableHistories.filter"
          :fields="tableHistories.fields"
          :stacked="tableHistories.stacked"
          :sort-by.sync="tableHistories.sortBy"
          :sort-desc.sync="tableHistories.sortDesc"
          :sort-direction="tableHistories.sortDirection"
          :filter-included-fields="tableHistories.filterOn"
          :current-page="tableHistories.currentPage"
          :per-page="tableHistories.perPage"
        >
          <template #cell(index)="{ index }">
            {{ tableRowNumber(tableHistories, index) }}
          </template>
          <template #row-details="row">
            <div class="p-2 bg-white">
              <BTable
                small
                hover
                bordered
                responsive
                show-empty
                class="col-md-6 col-12"
                :stacked="tableHistories.stacked"
                :items="row.item.approvable.transfer_details"
                :fields="[
                  { key: 'index', label: '#', class: 'text-center' },
                  { key: 'product_name', label: 'Product Name', class: 'col-md-6 col-12' },
                  { key: 'quantity', label: 'Ordered Quantity', class: 'text-center min-w-100px' },
                  { key: 'approved_quantity', label: 'Approved Quantity', class: 'text-center min-w-100px', formatter: approvedToPostingQuantity }
                ]"
              >
                <template #cell(index)="{ index }">
                  <div style="min-width: 25px;">
                    {{ tableRowNumber(tableHistories, index) }}
                  </div>
                </template>

                <template #cell(quantity)="{ value }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      class="font-weight-bolder"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>

                <template #cell(approved_quantity)="{ value, item }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      :class="['font-weight-bolder', {
                        'text-danger': Number(value) <= 0,
                        'text-warning': Number(value) >= 1 && Number(item.quantity) !== Number(value),
                        'text-success': Number(item.quantity) === Number(value)
                      }]"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>
              </BTable>
            </div>
          </template>

          <template #cell(action)="{ item, toggleDetails, detailsShowing }">
            <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-md-start justify-content-center">
              <BButton
                size="sm"
                variant="primary"
                class="mr-0 mr-md-1 mb-1 mb-md-0"
                :disabled="tableHistories.busy"
                @click.prevent="show(item)"
              >
                View
              </BButton>
              <BButton
                size="sm"
                :variant="detailsShowing ? 'danger' : 'warning' "
                :disabled="tableHistories.busy"
                @click.prevent="toggleDetails"
              >
                {{ detailsShowing ? 'Hide' : 'Show' }} Products
              </BButton>
            </div>
          </template>

          <template #cell(approvable.transaction_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(approvable.status)="{ value }">
            <b
              :class="[
                'min-w-80px',
                statusByVariant(value, 'text')
              ]"
              v-text="value"
            />
          </template>

          <template #cell(status)="{ value }">
            <b
              :class="[
                'min-w-80px',
                statusByVariant(value, 'text')
              ]"
              v-text="value"
            />
          </template>

          <template #cell()="{ value }">
            <div
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #table-busy>
            <div class="text-center py-5">
              <BIcon
                icon="stopwatch"
                font-scale="5"
                animation="cylon"
              />
              <p class="h3 py-2">
                <strong>Loading . . .</strong>
              </p>
            </div>
          </template>

        </BTable>
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-2 px-1"
      >
        <BFormSelect
          v-model="tableHistories.perPage"
          :options="tableHistories.pageOptions"
          :disabled="tableHistories.busy"
          class="w-100 w-md-25"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
      >
        <BPagination
          v-model="tableHistories.currentPage"
          :total-rows="tableHistories.totalRows"
          :per-page="tableHistories.perPage"
          :disabled="tableHistories.busy"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>

    </BRow>
  </BContainer>
</template>
<script>
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import { SWarehousePOApproval } from '@/services/user/wo'

export default {

  name: 'VWarehouseApprovalHistory',

  mixins: [
    MISC,
    FORMATTER
  ],

  props: {
    tabIndex: {
      type: Number,
      default: () => 0
    }
  },

  data () {
    return {
      exporter: {
        busy: false,
        excel: {
          exporting: false,
          header: [],
          escape: false,
          type: 'xls',
          fields: {
            'Schedule Date': {
              field: 'date'
            },
            'Plate Number': {
              field: 'vehicle_plate'
            },
            'Contact Number': {
              field: 'mobile_number'
            },
            Driver: {
              field: 'driver_name'
            },
            Helper: {
              field: 'helper'
            },
            Quantity: {
              field: 'posting_quantity'
            },
            UoM: {
              field: 'unit_of_measurement'
            },
            Product: {
              field: 'product_name'
            },
            'IT Number': {
              field: 'transfer_number'
            },
            'Deliver To': {
              field: 'destination_name'
            },
            'Delivery From': {
              field: 'origin_name'
            },
            'Trip Type': {
              field: 'trip_type'
            }
          },
          footer: []
        }
      },
      tableHistories: {
        busy: false,
        filter: {
          search: '',
          status: this.$route.params?.status || 'all',
          date_from: '',
          date_to: ''
        },
        filterOptions: {
          statuses: [
            { value: 'all', text: 'All' },
            { value: 'approved', text: 'Approved' },
            { value: 'disapproved', text: 'Disapproved' }
          ]
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 11, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'approvable.created_at', label: 'Ordered At', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'approvable.status', label: 'Order Status', class: 'text-center' },
          { mobile: 3, key: 'approvable.transaction_number', label: 'Transaction Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 4, key: 'approvable.destination_name', label: 'Ordered By', class: 'min-w-200px' },
          { mobile: 5, key: 'approvable.group_name', label: 'Group', class: 'min-w-200px' },
          { mobile: 6, key: 'approvable.transfer_details.length', label: 'Products', class: 'text-center' },
          { mobile: 7, key: 'approvable.delivery_details.customer', label: 'Customer', class: 'min-w-200px' },
          { mobile: 8, key: 'approvable.customer_direct', label: 'D2C', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 9, key: 'status', label: 'Approval Status', class: 'text-center' },
          { mobile: 20, key: 'status_at', label: 'Approval At', class: 'min-w-175px', formatter: this.dateTimeShortFormatter }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    excelFileName () {
      const deliveryDate = this.dateShortFormatter(this.tableHistories.filter.date_to)
      return `Scheduled Deliveries - ${deliveryDate}`
    },

    isValidForExport () {
      return !['', '', ' '].includes(this.tableHistories.filter.date_to) && Number(this.tableHistories.totalRows) >= 1
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableHistories.busy = true
      return await SWarehousePOApproval.histories({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_status: filter.status,
        filter_date_from: filter.date_from,
        filter_date_to: filter.date_to
      }).then(({ data }) => {
        this.tableHistories.totalRows = data.total_rows
        return data.items
      }).catch(() => {
        this.tableHistories.totalRows = 0
        return []
      }).finally(() => {
        this.tableHistories.busy = false
      })
    },

    tableRefresh () {
      this.$refs.tableHistories.refresh()
    },

    show (stockRequest) {
      this.$emit('show', stockRequest, false)
    },

    async excelProvider () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }
      this.exporter.busy = this.exporter.excel.exporting = true
      return await SWarehousePOApproval.exports({
        filter_text: this.tableHistories.filter.search,
        filter_date_from: this.tableHistories.filter.date_from,
        filter_date_to: this.tableHistories.filter.date_to,
        filter_status: this.tableHistories.filter.status
      }).then(({ data: { items } }) => {
        if (items.length <= 0) {
          this.swalInvalid(
            'No available data to be Exported'
          )
        }

        const reportFormat = []

        items.forEach(
          ({ approvable }) => {
            approvable.transfer_details.forEach(
              details => {
                reportFormat.push({
                  date: approvable.schedule_details.date,
                  vehicle_plate: approvable.schedule_details.vehicle_plate,
                  mobile_number: approvable.schedule_details.mobile_number,
                  driver_name: approvable.schedule_details.driver_name,
                  helper: approvable.schedule_details.helper,
                  posting_quantity: details.posting_quantity,
                  unit_of_measurement: Number(details.posting_quantity) > 1 ? 'PCS' : 'PC',
                  product_name: details.product_name,
                  transfer_number: approvable.transfer_number,
                  destination_name: approvable.destination_name,
                  origin_name: approvable.origin_name,
                  trip_type: approvable.schedule_details.trip_type
                })
              }
            )
          }
        )

        return reportFormat
      }).catch(() => {
        return []
      }).finally(() => {
        this.exporter.busy = this.exporter.excel.exporting = false
      })
    },

    onStartExcelExport () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.excel.header = []
      this.exporter.excel.footer = []
    },

    onFinishExcelExport () {
    }
  }
}
</script>
